import { CircularProgress, Stack } from "@mui/material"
import { Context, ReactElement, useContext } from "react"

import { Datasets } from "../../../../../constants/enums"
import { useFilterReferenceMappings } from "../../../../../hooks/useFilterReferenceMappings"
import useUploadedDeviceAttachments from "../../../../../services/middleware/hooks/devices/common/useUploadedDeviceAttachments"
import { GRID_SPACING } from "../../../../../themes/defaultThemeParams"
import {
    getDisplayName,
    goToPage,
} from "../../../../../utils/registerDevice/registerDeviceUtils"
import ErrorDisplay from "../../../../fallback/errorDisplay"
import FormHeader from "../../../../formHeader/formHeader"
import FormNavigation from "../../../../formNavigation/formNavigation"
import FormSummary from "../../../../formSummary/formSummary"
import Confirmation from "../../../../popUps/registerDevice/confirmation"
import { IDeviceFormContextValue } from "../../../types/providerTypes"
import {
    HP_BACK_UP_HEATER_MODULE_INPUT_RATED_CURRENT_REG,
    HP_BACK_UP_HEATER_MODULE_INPUT_RATED_POWER_REG,
    HP_BACK_UP_HEATER_MODULE_REF_REG,
    HP_BACK_UP_HEATER_MODULE_REG,
    HP_BACK_UP_HEATER_PHASES_REG,
    HP_BACK_UP_HEATER_PRESENT_REG,
    HP_BOOST_HEATER_MODULE_INPUT_RATED_CURRENT_REG,
    HP_BOOST_HEATER_MODULE_INPUT_RATED_POWER_REG,
    HP_BOOST_HEATER_MODULE_REF_REG,
    HP_BOOST_HEATER_MODULE_REG,
    HP_BOOST_HEATER_PHASES_REG,
    HP_BOOST_HEATER_PRESENT_REG,
    HP_FLICKER_STANDARDS_REG,
    HP_FUNCTION_REG,
    HP_HARMONIC_STANDARDS_REG,
    HP_HEAT_PUMP_MODULE_INPUT_RATED_CURRENT_REG,
    HP_HEAT_PUMP_MODULE_INPUT_RATED_POWER_REG,
    HP_HEAT_PUMP_MODULE_REF_REG,
    HP_HEAT_PUMP_MODULE_REG,
    HP_HEAT_PUMP_PHASES_REG,
    HP_HEAT_PUMP_PRESENT_REG,
    HP_MANUFACTURER_REG,
    HP_MAX_DEMAND_CURRENT_REG,
    HP_MAX_DEMAND_POWER_REG,
    HP_MODEL_REF_REG,
    HP_MODEL_REG,
    HP_MODULE_REF_REG,
    HP_MODULE_REG,
    HP_NOM_CURRENT_W35_REG,
    HP_NOM_CURRENT_W45_REG,
    HP_NOM_CURRENT_W55_REG,
    HP_NOM_CURRENT_W65_REG,
    HP_PHASES_REG,
    HP_TOT_INPUT_RATED_CURRENT_REG,
    HP_TOT_INPUT_RATED_POWER_REG,
    HP_VOLTAGE_REG,
    HP_WATER_HEATER_MODULE_INPUT_RATED_CURRENT_REG,
    HP_WATER_HEATER_MODULE_INPUT_RATED_POWER_REG,
    HP_WATER_HEATER_MODULE_REF_REG,
    HP_WATER_HEATER_MODULE_REG,
    HP_WATER_HEATER_PHASES_REG,
    HP_WATER_HEATER_PRESENT_REG,
    IS_MODULE_PRESENT,
    hpDeviceFormLabelMapping,
} from "../../formSpecs/hpSpec"
import { HpDeviceFormData } from "../../types/providerTypes"

interface IReviewAndSubmitProps {
    context: Context<IDeviceFormContextValue<HpDeviceFormData>>
    isAmendment: boolean
}

const ReviewAndSubmitHpDevice = ({
    context,
    isAmendment,
}: IReviewAndSubmitProps): ReactElement => {
    const {
        formData,
        deviceRef,
        currentIndex,
        setCurrentIndex,
        onSubmit,
        submitLoading,
        submitSuccessful,
        canEditManufacturer,
        attachmentUUID,
        errors: contextErrors,
    } = useContext(context)

    const { manufacturerMappings } = useFilterReferenceMappings()

    const {
        uploadedAttachments,
        error: listAttachmentsError,
        isLoading: isLoadingUploadedAttachments,
        isValidating: isValidatingUploadedAttachments,
    } = useUploadedDeviceAttachments(
        Datasets.HP,
        deviceRef,
        attachmentUUID,
        isAmendment
    )

    const uploadedAttachmentsAreValid =
        !isLoadingUploadedAttachments &&
        !isValidatingUploadedAttachments &&
        !listAttachmentsError &&
        !!uploadedAttachments

    const showPageEdit = !submitLoading

    const goToAttachmentsPage = (): void =>
        goToPage(2, canEditManufacturer, setCurrentIndex)

    const goToCoreHpDeviceDetailsPage = (): void =>
        goToPage(1, canEditManufacturer, setCurrentIndex)

    const goToCoreHpManufacturerPage = (): void =>
        goToPage(0, canEditManufacturer, setCurrentIndex)

    const onClickBack = (): void => setCurrentIndex(currentIndex - 1)

    const showManufacturerEdit = canEditManufacturer && !submitLoading

    const attachmentMappings: Record<
        string,
        string | number | boolean | undefined
    > = {}

    if (uploadedAttachmentsAreValid) {
        uploadedAttachments.forEach((summary) => {
            attachmentMappings[summary.fileName] = summary.description
        })
    }

    if (contextErrors.length > 0) {
        return <ErrorDisplay error={contextErrors[0]} />
    }

    if (listAttachmentsError) {
        return <ErrorDisplay error={listAttachmentsError} />
    }

    return (
        <>
            <Confirmation
                isOpen={submitSuccessful}
                deviceRef={deviceRef}
                dataset={Datasets.HP}
            />
            <Stack spacing={GRID_SPACING} paddingTop={GRID_SPACING}>
                <FormHeader
                    header="Review and Submit"
                    subHeader="Please check through your details prior to submitting"
                    index={currentIndex + 1}
                    headerTypography={{
                        color: "text.primary",
                    }}
                />
                <FormSummary
                    summaries={[
                        {
                            title: "Manufacturer",
                            mappings: {
                                [hpDeviceFormLabelMapping[HP_MANUFACTURER_REG]]:
                                    formData.manufacturerId
                                        ? getDisplayName(
                                              manufacturerMappings,
                                              Number(formData.manufacturerId)
                                          )
                                        : "Not found",
                            },
                            onEdit: showManufacturerEdit
                                ? goToCoreHpManufacturerPage
                                : undefined,
                            omit: !canEditManufacturer,
                        },

                        {
                            title: "Core Heat Pump Device Details",
                            mappings: {
                                [hpDeviceFormLabelMapping[HP_MODEL_REG]]:
                                    formData.deviceModel,

                                [hpDeviceFormLabelMapping[HP_MODEL_REF_REG]]:
                                    formData.deviceModelRef,

                                [hpDeviceFormLabelMapping[HP_FUNCTION_REG]]:
                                    formData.function ?? undefined,

                                [hpDeviceFormLabelMapping[HP_PHASES_REG]]:
                                    formData.devicePhases ?? undefined,

                                [hpDeviceFormLabelMapping[HP_VOLTAGE_REG]]:
                                    formData.deviceVoltage ?? undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_NOM_CURRENT_W35_REG
                                ]]: formData.deviceNomCurrentW35 ?? undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_NOM_CURRENT_W45_REG
                                ]]: formData.deviceNomCurrentW45 ?? undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_NOM_CURRENT_W55_REG
                                ]]: formData.deviceNomCurrentW55 ?? undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_NOM_CURRENT_W65_REG
                                ]]: formData.deviceNomCurrentW65 ?? undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_TOT_INPUT_RATED_CURRENT_REG
                                ]]:
                                    formData.deviceTotInputRatedCurrent ??
                                    undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_TOT_INPUT_RATED_POWER_REG
                                ]]:
                                    formData.deviceTotInputRatedPower ??
                                    undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_MAX_DEMAND_CURRENT_REG
                                ]]:
                                    formData.deviceMaxDemandCurrent ??
                                    undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_MAX_DEMAND_POWER_REG
                                ]]: formData.deviceMaxDemandPower ?? undefined,

                                [hpDeviceFormLabelMapping[HP_MODULE_REG]]:
                                    formData.deviceModule ?? undefined,

                                [hpDeviceFormLabelMapping[HP_MODULE_REF_REG]]:
                                    formData.deviceModelRef,

                                [hpDeviceFormLabelMapping[
                                    HP_HARMONIC_STANDARDS_REG
                                ]]:
                                    formData.deviceHarmonicStandards ??
                                    undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_FLICKER_STANDARDS_REG
                                ]]:
                                    formData.deviceFlickerStandards ??
                                    undefined,

                                [hpDeviceFormLabelMapping[
                                    HP_BACK_UP_HEATER_PRESENT_REG
                                ]]:
                                    formData.deviceIsBackUpHeaterPresent ??
                                    undefined,

                                ...(formData.deviceIsBackUpHeaterPresent ===
                                    IS_MODULE_PRESENT && {
                                    [hpDeviceFormLabelMapping[
                                        HP_BACK_UP_HEATER_PHASES_REG
                                    ]]:
                                        formData.deviceBackUpHeaterPhases ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_BACK_UP_HEATER_MODULE_REF_REG
                                    ]]:
                                        formData.deviceBackUpHeaterModuleRef ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_BACK_UP_HEATER_MODULE_REG
                                    ]]:
                                        formData.deviceBackUpHeaterModule ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_BACK_UP_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                                    ]]:
                                        formData.deviceBackUpHeaterModuleInputRatedCurrent ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_BACK_UP_HEATER_MODULE_INPUT_RATED_POWER_REG
                                    ]]:
                                        formData.deviceBackUpHeaterModuleInputRatedPower ??
                                        undefined,
                                }),

                                [hpDeviceFormLabelMapping[
                                    HP_BOOST_HEATER_PRESENT_REG
                                ]]:
                                    formData.deviceIsBoostHeaterPresent ??
                                    undefined,

                                ...(formData.deviceIsBoostHeaterPresent ===
                                    IS_MODULE_PRESENT && {
                                    [hpDeviceFormLabelMapping[
                                        HP_BOOST_HEATER_PHASES_REG
                                    ]]:
                                        formData.deviceBoostHeaterPhases ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_BOOST_HEATER_MODULE_REF_REG
                                    ]]:
                                        formData.deviceBoostHeaterModuleRef ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_BOOST_HEATER_MODULE_REG
                                    ]]:
                                        formData.deviceBoostHeaterModule ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_BOOST_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                                    ]]:
                                        formData.deviceBoostHeaterModuleInputRatedCurrent ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_BOOST_HEATER_MODULE_INPUT_RATED_POWER_REG
                                    ]]:
                                        formData.deviceBoostHeaterModuleInputRatedPower ??
                                        undefined,
                                }),

                                [hpDeviceFormLabelMapping[
                                    HP_WATER_HEATER_PRESENT_REG
                                ]]:
                                    formData.deviceIsWaterHeaterPresent ??
                                    undefined,

                                ...(formData.deviceIsWaterHeaterPresent ===
                                    IS_MODULE_PRESENT && {
                                    [hpDeviceFormLabelMapping[
                                        HP_WATER_HEATER_PHASES_REG
                                    ]]:
                                        formData.deviceWaterHeaterPhases ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_WATER_HEATER_MODULE_REF_REG
                                    ]]:
                                        formData.deviceWaterHeaterModuleRef ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_WATER_HEATER_MODULE_REG
                                    ]]:
                                        formData.deviceWaterHeaterModule ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_WATER_HEATER_MODULE_INPUT_RATED_CURRENT_REG
                                    ]]:
                                        formData.deviceWaterHeaterModuleInputRatedCurrent ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_WATER_HEATER_MODULE_INPUT_RATED_POWER_REG
                                    ]]:
                                        formData.deviceWaterHeaterModuleInputRatedPower ??
                                        undefined,
                                }),

                                [hpDeviceFormLabelMapping[
                                    HP_HEAT_PUMP_PRESENT_REG
                                ]]:
                                    formData.deviceIsHeatPumpModulePresent ??
                                    undefined,

                                ...(formData.deviceIsHeatPumpModulePresent ===
                                    IS_MODULE_PRESENT && {
                                    [hpDeviceFormLabelMapping[
                                        HP_HEAT_PUMP_PHASES_REG
                                    ]]:
                                        formData.deviceHeatPumpModulePhases ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_HEAT_PUMP_MODULE_REF_REG
                                    ]]:
                                        formData.deviceHeatPumpModuleRef ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_HEAT_PUMP_MODULE_REG
                                    ]]:
                                        formData.deviceHeatPumpModule ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_HEAT_PUMP_MODULE_INPUT_RATED_CURRENT_REG
                                    ]]:
                                        formData.deviceHeatPumpModuleInputRatedCurrent ??
                                        undefined,

                                    [hpDeviceFormLabelMapping[
                                        HP_HEAT_PUMP_MODULE_INPUT_RATED_POWER_REG
                                    ]]:
                                        formData.deviceHeatPumpModuleInputRatedPower ??
                                        undefined,
                                }),
                            },
                            onEdit: showPageEdit
                                ? goToCoreHpDeviceDetailsPage
                                : undefined,
                        },
                        {
                            title: `Files - ${
                                uploadedAttachmentsAreValid
                                    ? uploadedAttachments.length
                                    : "Loading"
                            }`,
                            mappings: attachmentMappings,
                            onEdit: showPageEdit
                                ? goToAttachmentsPage
                                : undefined,
                        },
                    ]}
                />

                <FormNavigation
                    onClickBack={onClickBack}
                    onClickNext={onSubmit}
                    nextText="Submit"
                    backText="Back"
                    nextDisabled={submitLoading}
                    backDisabled={submitLoading}
                    nextIcon={
                        submitLoading ? (
                            <CircularProgress size={20} />
                        ) : undefined
                    }
                />
            </Stack>
        </>
    )
}

export default ReviewAndSubmitHpDevice
